import React from 'react';
import { PUBLISH_DELIVERY_ENDPOINT } from '../../../../fetch-data/graphql/config';
import { FontWeight } from '../../../../utils/typography';
import { Icon } from '../../../Icon';
import { IconImage } from './overrides/typography';

// Helper to check if children have actual content (not just whitespace/empty spans)
export const hasActualContent = (children: React.ReactNode): boolean => {
  if (!children) return false;

  const childrenArray = React.Children.toArray(children);

  for (const child of childrenArray) {
    if (typeof child === 'string') {
      // Check if string has non-whitespace content (excluding &nbsp;)
      const trimmed = child.replace(/&nbsp;/g, '').trim();
      if (trimmed.length > 0) {
        return true;
      }
    } else if (React.isValidElement(child)) {
      const props = child.props as { children?: React.ReactNode };
      // Recursively check children
      if (props?.children && hasActualContent(props.children)) {
        return true;
      }
      // If element has no children, check if it's a meaningful element (not just a span)
      if (!props?.children) {
        const childType = child.type;
        // If it's not a span or div, it might be meaningful (img, br, etc.)
        if (
          typeof childType === 'string' &&
          !['span', 'div'].includes(childType)
        ) {
          return true;
        }
      }
    } else {
      // Other types (numbers, etc.) are considered content
      return true;
    }
  }

  return false;
};

export const hasStrongTag = (children: React.ReactNode): boolean => {
  if (!children) return false;

  return React.Children.toArray(children).some((child) => {
    if (typeof child === 'string') return false;

    if (React.isValidElement(child)) {
      const childType = child.type;

      // Check if type is 'strong' - handle string, function, or symbol types
      let isStrong = false;

      // Direct comparison (works for native HTML elements)
      if (childType === 'strong') {
        isStrong = true;
      }
      // String type check
      else if (typeof childType === 'string' && childType === 'strong') {
        isStrong = true;
      }
      // Function component check
      else if (typeof childType === 'function') {
        isStrong =
          childType.name === 'strong' ||
          (childType as any).displayName === 'strong';
      }
      // Object type (for forwarded refs, memo, etc.)
      else if (childType && typeof childType === 'object') {
        const typeObj = childType as any;
        // Check displayName, name, or if it's a wrapper around 'strong'
        isStrong =
          typeObj.displayName === 'strong' ||
          typeObj.name === 'strong' ||
          (typeObj.type && typeObj.type === 'strong');
      }

      // Convert to string and check (fallback for edge cases)
      if (!isStrong) {
        const typeString = String(childType);
        if (typeString === 'strong' || typeString.includes('strong')) {
          isStrong = true;
        }
      }

      // Also check props for tag indication
      if (!isStrong && (child.props as any)?.tag === 'strong') {
        isStrong = true;
      }

      if (isStrong) {
        return true;
      }

      // Recursively check nested children
      if (child.props?.children) {
        return hasStrongTag(child.props.children);
      }
    }

    return false;
  });
};

export const normalizeSrc = (src?: string) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return `${PUBLISH_DELIVERY_ENDPOINT}${src}`;
};

export const is2x = (src?: string) => {
  if (!src) return false;
  return /2x|@2x/i.test(src);
};

export const hasLineBreakTag = (children: React.ReactNode): boolean => {
  if (!children) return false;

  return React.Children.toArray(children).some((child) => {
    if (typeof child === 'string') return false;

    if (React.isValidElement(child)) {
      const childType = child.type;

      // Check if type is 'br'
      const isBr =
        childType === 'br' ||
        (typeof childType === 'string' && childType === 'br') ||
        String(childType) === 'br';

      if (isBr) return true;

      // Recursively check nested children
      if (child.props?.children) {
        return hasLineBreakTag(child.props.children);
      }
    }

    return false;
  });
};

export const processChildrenWithLineBreaks = (
  children: React.ReactNode
): React.ReactNode => {
  if (!children) return children;

  const childrenArray = React.Children.toArray(children);

  // Find the last <br/> tag index
  let lastBrIndex = -1;
  for (let i = childrenArray.length - 1; i >= 0; i--) {
    const child = childrenArray[i];
    if (React.isValidElement(child)) {
      const childType = child.type;
      const isBr =
        childType === 'br' ||
        (typeof childType === 'string' && childType === 'br') ||
        String(childType) === 'br';

      if (isBr) {
        lastBrIndex = i;
        break;
      }
    }
  }

  // If no <br/> found, return as is
  if (lastBrIndex === -1) return children;

  // Split into parts: before last <br/> (light) and after last <br/> (bold)
  const beforeBr = childrenArray.slice(0, lastBrIndex + 1); // Include the <br/>
  const afterBr = childrenArray.slice(lastBrIndex + 1);

  return React.createElement(
    React.Fragment,
    null,
    React.createElement(
      'span',
      { style: { fontWeight: FontWeight.light } },
      beforeBr
    ),
    afterBr.length > 0 &&
      React.createElement(
        'span',
        { style: { fontWeight: FontWeight.bold } },
        afterBr
      )
  );
};

export const hasBlockElements = (children: React.ReactNode): boolean => {
  return React.Children.toArray(children).some(
    (child: any) =>
      React.isValidElement(child) &&
      typeof child.type === 'string' &&
      ['p', 'ul', 'ol', 'div'].includes(child.type)
  );
};

// Helper to check if a component is a block element
const isBlockElement = (child: any): boolean => {
  if (!React.isValidElement(child)) return false;

  const childType = child.type;

  // Check if it's a string type (native HTML element)
  if (typeof childType === 'string') {
    return ['p', 'ul', 'ol', 'div'].includes(childType);
  }

  // Check if it's a React component (function or object)
  if (typeof childType === 'function') {
    const func = childType as any;
    const props = child.props as { isWrapper?: boolean };

    // Check if it's Paragraph component (and NOT a wrapper)
    if (
      (func.name === 'Paragraph' || func.displayName === 'Paragraph') &&
      !props?.isWrapper
    ) {
      return true;
    }

    // Check if it's UL component
    if (func.name === 'UL' || func.displayName === 'UL') {
      return true;
    }

    // Check if it's OL component
    if (func.name === 'OL' || func.displayName === 'OL') {
      return true;
    }
  }

  return false;
};

export const separateInlineAndBlock = (
  children: React.ReactNode
): { inline: React.ReactNode[]; block: React.ReactNode[] } => {
  const childrenArray = React.Children.toArray(children);
  const inline: React.ReactNode[] = [];
  const block: React.ReactNode[] = [];

  childrenArray.forEach((child: any) => {
    if (isBlockElement(child)) {
      block.push(child);
    } else {
      inline.push(child);
    }
  });

  return { inline, block };
};

// Helper to check if an element contains block elements (up to 3 levels deep)
const hasBlockElementNested = (
  element: React.ReactElement,
  depth: number = 0
): boolean => {
  if (depth > 3) return false; // Only check 3 sublevels

  const props = element.props as { children?: React.ReactNode };
  if (!props?.children) return false;

  const childrenArray = React.Children.toArray(props.children);

  for (const child of childrenArray) {
    if (isBlockElement(child)) {
      return true;
    }

    if (React.isValidElement(child)) {
      if (hasBlockElementNested(child, depth + 1)) {
        return true;
      }
    }
  }

  return false;
};

// Wraps inline content that is not inside block elements in Paragraph isWrapper={false}
// Only processes up to 3 sublevels deep
export const wrapInlineContentInParagraph = (
  children: React.ReactNode,
  ParagraphComponent: any
): React.ReactNode[] => {
  const childrenArray = React.Children.toArray(children);
  const result: React.ReactNode[] = [];
  let currentInlineGroup: React.ReactNode[] = [];

  const flushInlineGroup = () => {
    if (currentInlineGroup.length > 0) {
      result.push(
        React.createElement(
          ParagraphComponent,
          { isWrapper: false, key: `inline-${result.length}` },
          ...currentInlineGroup
        )
      );
      currentInlineGroup = [];
    }
  };

  childrenArray.forEach((child: any, index: number) => {
    if (isBlockElement(child)) {
      // Flush any accumulated inline content before adding block element
      flushInlineGroup();
      result.push(child);
    } else if (React.isValidElement(child)) {
      // Check if this element contains block elements (up to 3 levels)
      if (hasBlockElementNested(child, 0)) {
        // This element contains blocks, process it recursively
        flushInlineGroup();
        const props = child.props as { children?: React.ReactNode };
        const processedChildren = wrapInlineContentInParagraph(
          props.children,
          ParagraphComponent
        );
        result.push(
          React.cloneElement(
            child,
            { key: child.key || `element-${index}` } as any,
            ...processedChildren
          )
        );
      } else {
        // This element doesn't contain blocks, add to inline group
        currentInlineGroup.push(child);
      }
    } else {
      // Text or other non-element, add to inline group
      currentInlineGroup.push(child);
    }
  });

  // Flush any remaining inline content
  flushInlineGroup();

  return result;
};

// Helper to check if children contain [@MARKER] tag
export const hasMarkerTag = (children: React.ReactNode): boolean => {
  const childrenArray = React.Children.toArray(children);

  for (const child of childrenArray) {
    if (typeof child === 'string' && child.includes('[@MARKER]')) {
      return true;
    }
    if (React.isValidElement(child)) {
      const props = child.props as { children?: React.ReactNode };
      if (props?.children && hasMarkerTag(props.children)) {
        return true;
      }
    }
  }

  return false;
};

// Helper to find the first non-text child in an array
export const findFirstNonTextChild = (
  children: React.ReactNode[]
): { index: number; child: React.ReactElement | null } => {
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (React.isValidElement(child)) {
      return { index: i, child };
    }
  }
  return { index: -1, child: null };
};

// Helper to remove [@MARKER] tag from React nodes recursively
export const removeMarkerTag = (node: React.ReactNode): React.ReactNode => {
  if (typeof node === 'string') {
    return node.replace(/\[@MARKER\]/g, '');
  }
  if (React.isValidElement(node)) {
    const props = node.props as { children?: React.ReactNode };
    if (props?.children) {
      const processedChildren = React.Children.map(
        props.children,
        removeMarkerTag
      );
      if (processedChildren) {
        // Handle single child vs array
        const childrenToPass =
          processedChildren.length === 1
            ? processedChildren[0]
            : processedChildren;
        return React.cloneElement(node, {} as any, childrenToPass);
      }
    }
  }
  return node;
};

export const isFirstChildAnIcon = (
  firstChild: any,
  skipLists: boolean = false
): boolean => {
  if (!React.isValidElement(firstChild)) return false;

  const componentType = firstChild.type;

  // Don't recurse into nested lists (ul/ol)
  if (skipLists && (componentType === 'ul' || componentType === 'ol')) {
    return false;
  }

  // Check if the first child itself is an icon
  if (isIconComponent(componentType)) {
    return true;
  }

  // If the first child is a wrapper, get the first non-text child and check recursively
  const props = firstChild.props as { children?: React.ReactNode };
  if (props?.children) {
    const childrenArray = React.Children.toArray(props.children);

    // Find the first non-text child
    for (const child of childrenArray) {
      if (React.isValidElement(child)) {
        const nestedComponentType = child.type;

        // Don't recurse into nested lists
        if (
          skipLists &&
          (nestedComponentType === 'ul' || nestedComponentType === 'ol')
        ) {
          continue;
        }

        if (isIconComponent(nestedComponentType)) {
          return true;
        }
        // Recursively check nested children (for deeply nested cases)
        return isFirstChildAnIcon(child, skipLists);
      }
      // Skip text nodes and continue to next child
    }
  }

  return false;
};

export const extractIconFromWrapper = (
  firstChild: any,
  skipLists: boolean = false
): {
  icon: React.ReactElement | null;
  remainingWrapperChildren: React.ReactNode[];
} => {
  if (!React.isValidElement(firstChild)) {
    return { icon: null, remainingWrapperChildren: [] };
  }

  const componentType = firstChild.type;

  // Don't recurse into nested lists
  if (skipLists && (componentType === 'ul' || componentType === 'ol')) {
    return { icon: null, remainingWrapperChildren: [] };
  }

  // If the first child itself is an icon, return it with no remaining children
  if (isIconComponent(componentType)) {
    return { icon: firstChild, remainingWrapperChildren: [] };
  }

  // If the first child is a wrapper, find the first non-text child and extract icon
  const props = firstChild.props as { children?: React.ReactNode };
  if (props?.children) {
    const childrenArray = React.Children.toArray(props.children);

    // Find the first non-text child (skip nested lists if skipLists is true)
    let firstNonTextIndex = -1;
    let firstNonTextChild: React.ReactElement | null = null;

    for (let i = 0; i < childrenArray.length; i++) {
      const child = childrenArray[i];
      if (React.isValidElement(child)) {
        const childType = child.type;
        // Skip nested lists if skipLists is true
        if (skipLists && (childType === 'ul' || childType === 'ol')) {
          continue;
        }
        firstNonTextIndex = i;
        firstNonTextChild = child;
        break;
      }
    }

    if (firstNonTextChild) {
      const nestedComponentType = firstNonTextChild.type;
      if (isIconComponent(nestedComponentType)) {
        // Extract the icon and get remaining children (text before + siblings after)
        const beforeIcon = childrenArray.slice(0, firstNonTextIndex);
        const afterIcon = childrenArray.slice(firstNonTextIndex + 1);
        const remainingChildren = [...beforeIcon, ...afterIcon];

        // If there are remaining children, preserve the wrapper
        if (remainingChildren.length > 0) {
          return {
            icon: firstNonTextChild,
            remainingWrapperChildren: [
              React.cloneElement(firstChild, {} as any, ...remainingChildren),
            ],
          };
        }

        // No remaining children, just return the icon
        return {
          icon: firstNonTextChild,
          remainingWrapperChildren: [],
        };
      }

      // Recursively extract from nested children
      const result = extractIconFromWrapper(firstNonTextChild, skipLists);
      if (result.icon) {
        // Build remaining children: text before, result's remaining, text/siblings after
        const beforeIcon = childrenArray.slice(0, firstNonTextIndex);
        const afterIcon = childrenArray.slice(firstNonTextIndex + 1);

        const allRemainingChildren = [
          ...beforeIcon,
          ...(result.remainingWrapperChildren.length > 0
            ? result.remainingWrapperChildren
            : []),
          ...afterIcon,
        ];

        // If there are remaining children, preserve the wrapper
        if (allRemainingChildren.length > 0) {
          return {
            icon: result.icon,
            remainingWrapperChildren: [
              React.cloneElement(
                firstChild,
                {} as any,
                ...allRemainingChildren
              ),
            ],
          };
        }

        // No remaining children, return as-is
        return {
          icon: result.icon,
          remainingWrapperChildren: result.remainingWrapperChildren,
        };
      }
    }
  }

  return { icon: null, remainingWrapperChildren: [] };
};

export const isIconComponent = (component: any): boolean => {
  if (!component) return false;
  if (component === Icon || component === IconImage) return true;
  if (typeof component === 'function') {
    const func = component as any;
    return (
      func.name === 'Icon' ||
      func.name === 'IconImage' ||
      func.displayName === 'Icon' ||
      func.displayName === 'IconImage'
    );
  }
  return false;
};

/**
 * Checks if a URL is external (not a relative path)
 */
export const isExternalUrl = (url: string): boolean => {
  if (!url || url === '#') return false;

  // URLs with protocol are external
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return true;
  }

  // URLs starting with www. are external
  if (url.startsWith('www.')) {
    return true;
  }

  // URLs that look like domains (contain a dot and don't start with / or #)
  // Pattern: something.something (e.g., youtube.com, example.com)
  const domainPattern = /^[^/?#]+\.[^/?#]+/;
  if (domainPattern.test(url) && !url.startsWith('/') && !url.startsWith('#')) {
    return true;
  }

  return false;
};

/**
 * Validates if a URL is valid and can be used
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || url === '#') return false;

  try {
    // If it has a protocol, try to create a URL object
    if (url.startsWith('http://') || url.startsWith('https://')) {
      new URL(url);
      return true;
    }

    // If it's a domain-like URL without protocol, try with https://
    if (isExternalUrl(url)) {
      new URL(`https://${url}`);
      return true;
    }

    // Relative URLs are considered valid
    return true;
  } catch {
    return false;
  }
};

/**
 * Normalizes external URLs by adding https:// if missing
 */
export const normalizeUrl = (url: string): string => {
  if (!url || url === '#') return '#';

  // If it already has a protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If it's an external URL without protocol, add https://
  if (isExternalUrl(url)) {
    return `https://${url}`;
  }

  return url;
};
